import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Bike, ArrowLeft, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { deliveryAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Dedicated delivery rider signup form.
 *
 * UK compliance for food delivery is non trivial: HMRC needs the NI
 * number for digital platform reporting, the Rehabilitation of
 * Offenders Act requires DBS consent, and motorised vehicles need
 * hire and reward insurance plus a valid MOT, V5C, and road tax.
 *
 * Rather than dumping every field on the user at once, the page is
 * sectioned so each block is readable and the motorised only fields
 * are only shown when the rider picks moped, motorcycle, or car.
 *
 * Bank details deferred to the dashboard's payout setup screen — there
 * is no point asking for them up front when the rider cannot actually
 * earn until their compliance docs are verified.
 */

const Section = ({ title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <p className="text-sm font-semibold text-[#1F3A68] mb-0.5">{title}</p>
    {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
    <div className="space-y-3">{children}</div>
  </div>
);

const DeliveryRiderSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, user: authedUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const isAddRoleMode = !!authedUser;

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    city: '',
    deliveryMethod: 'bicycle',
    dateOfBirth: '',
    nationalInsuranceNumber: '',
    rightToWorkStatus: '',
    crcConsent: false,
    classificationAccepted: false,
    // Vehicle (motorized only)
    vehicleMake: '',
    vehicleModel: '',
    vehicleColour: '',
    vehicleYear: '',
    vehicleRegistrationPlate: '',
    vehicleMOTExpiry: '',
    vehicleInsuranceProvider: '',
    vehicleInsurancePolicyNumber: '',
    vehicleInsuranceExpiry: '',
  });

  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
  }, [location.search]);

  useEffect(() => {
    if (isAddRoleMode && authedUser?.name && !form.fullName) {
      setForm(prev => ({
        ...prev,
        fullName: authedUser.name,
        dateOfBirth: prev.dateOfBirth || authedUser.dateOfBirth || ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddRoleMode]);

  const isMotorised = ['moped', 'motorcycle', 'car'].includes(form.deliveryMethod);

  const ageFromDob = (dobStr) => {
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAddRoleMode) {
      if (form.password !== form.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
      if (!form.email || !form.password) {
        toast({ title: 'Error', description: 'Email and password are required', variant: 'destructive' });
        return;
      }
    }

    if (!form.fullName || !form.phone || !form.city) {
      toast({ title: 'Error', description: 'Name, phone, and city are required', variant: 'destructive' });
      return;
    }

    if (!form.crcConsent) {
      toast({
        title: 'Background check consent required',
        description: 'You must consent to a criminal record check to register as a delivery rider.',
        variant: 'destructive',
      });
      return;
    }

    if (!form.classificationAccepted) {
      toast({
        title: 'Working relationship declaration required',
        description: 'Please confirm the self-employed contractor declaration to continue.',
        variant: 'destructive',
      });
      return;
    }

    // Age gate — 18+ for all riders, 19+ for motorised (enforced server-side too).
    if (!form.dateOfBirth) {
      toast({ title: 'Date of birth required', description: 'You must be 18 or older to deliver with Ariv.', variant: 'destructive' });
      return;
    }
    const age = ageFromDob(form.dateOfBirth);
    if (age === null || age < 18) {
      toast({ title: 'Not eligible', description: 'You must be 18 or older to deliver with Ariv.', variant: 'destructive' });
      return;
    }
    if (isMotorised && age < 19) {
      toast({
        title: 'Not eligible for motorised delivery',
        description: 'You must be 19 or older to deliver by moped, motorcycle or car. Bicycle and e-bike delivery is open from 18.',
        variant: 'destructive',
      });
      return;
    }

    if (isMotorised) {
      if (!form.vehicleRegistrationPlate) {
        toast({ title: 'Vehicle plate required', description: 'Enter your vehicle registration plate.', variant: 'destructive' });
        return;
      }
      if (!form.vehicleInsuranceProvider || !form.vehicleInsurancePolicyNumber) {
        toast({ title: 'Insurance details required', description: 'Motorised riders need hire & reward insurance to operate.', variant: 'destructive' });
        return;
      }
    }

    setLoading(true);
    try {
      await deliveryAPI.register({
        email:    isAddRoleMode ? authedUser.email : form.email,
        password: isAddRoleMode ? 'addrole-placeholder-ignored' : form.password,
        fullName: form.fullName,
        phone:    form.phone,
        city:     form.city,
        deliveryMethod: form.deliveryMethod,
        dateOfBirth: form.dateOfBirth,
        nationalInsuranceNumber: form.nationalInsuranceNumber || null,
        rightToWorkStatus: form.rightToWorkStatus || null,
        crcConsent: form.crcConsent,
        classificationAccepted: form.classificationAccepted,
        vehicleType: isMotorised ? form.deliveryMethod : null,
        vehicleMake:   isMotorised ? form.vehicleMake   || null : null,
        vehicleModel:  isMotorised ? form.vehicleModel  || null : null,
        vehicleColour: isMotorised ? form.vehicleColour || null : null,
        vehicleYear:   isMotorised ? form.vehicleYear   || null : null,
        vehicleRegistrationPlate: isMotorised ? form.vehicleRegistrationPlate : null,
        vehicleMOTExpiry:         isMotorised ? form.vehicleMOTExpiry || null : null,
        vehicleInsuranceProvider:     isMotorised ? form.vehicleInsuranceProvider     : null,
        vehicleInsurancePolicyNumber: isMotorised ? form.vehicleInsurancePolicyNumber : null,
        vehicleInsuranceExpiry:       isMotorised ? form.vehicleInsuranceExpiry || null : null,
        vehicleInsuranceType:         isMotorised ? 'hire_and_reward' : null,
        referredBy: localStorage.getItem('bc_ref_code') || undefined,
      });

      if (isAddRoleMode) {
        toast({ title: 'Delivery Rider role added', description: 'Welcome. Your documents will be reviewed shortly.' });
        try { await refreshUser?.(); } catch (_) {}
      } else {
        toast({ title: 'Success', description: 'Rider account created. Logging you in…' });
        await login(form.email, form.password);
      }
      navigate('/delivery/dashboard');
    } catch (err) {
      toast({
        title: 'Registration failed',
        description: err.response?.data?.detail || 'Could not register delivery rider',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-start justify-center p-4 py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <Button variant="ghost" size="icon" className="absolute left-4 top-4" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <div className="mx-auto mb-4 p-3 bg-rose-600 rounded-full w-fit">
            <Bike size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl text-[#1F3A68]">
            {isAddRoleMode ? 'Add Delivery Rider role' : 'Delivery Rider Signup'}
          </CardTitle>
          <CardDescription>Earn flexibly delivering food orders across the city</CardDescription>
        </CardHeader>
        <CardContent>
          {isAddRoleMode && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Delivery Rider role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">Linked to {authedUser?.email}.</p>
            </div>
          )}

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900 flex items-start gap-2">
            <Info size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              We have to collect this information because UK law requires it for anyone delivering food
              (same as Deliveroo, Uber Eats, Just Eat). Bank details for payouts are set up later from
              your dashboard once your documents are verified.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Section title="About you">
              <div>
                <Label>Full name *</Label>
                <Input value={form.fullName} onChange={e => setForm(p => ({...p, fullName: e.target.value}))} placeholder="John Smith" />
              </div>
              {!isAddRoleMode && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="rider@example.com" />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+44 123 456 7890" />
                  </div>
                </div>
              )}
              {!isAddRoleMode && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="••••••••" />
                  </div>
                  <div>
                    <Label>Confirm password *</Label>
                    <Input type="password" value={form.confirmPassword} onChange={e => setForm(p => ({...p, confirmPassword: e.target.value}))} placeholder="••••••••" />
                  </div>
                </div>
              )}
              {isAddRoleMode && (
                <div>
                  <Label>Phone *</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+44 123 456 7890" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>City *</Label>
                  <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} placeholder="London" />
                </div>
                <div>
                  <Label>Date of birth *</Label>
                  <Input
                    type="date"
                    value={form.dateOfBirth}
                    onChange={e => setForm(p => ({...p, dateOfBirth: e.target.value}))}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 mt-1">18+ required (19+ for moped, motorcycle or car).</p>
                </div>
              </div>
            </Section>

            <Section title="How will you deliver?" subtitle="Pick one. Motorised options unlock the vehicle section below.">
              <select
                value={form.deliveryMethod}
                onChange={e => setForm(p => ({...p, deliveryMethod: e.target.value}))}
                className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm"
              >
                <option value="bicycle">Bicycle</option>
                <option value="ebike">E bike</option>
                <option value="moped">Moped (motorised)</option>
                <option value="motorcycle">Motorcycle (motorised)</option>
                <option value="car">Car (motorised)</option>
              </select>
            </Section>

            <Section title="Right to work in the UK" subtitle="Required by law for anyone who earns on a digital platform.">
              <div>
                <Label>National Insurance number</Label>
                <Input
                  value={form.nationalInsuranceNumber}
                  onChange={e => setForm(p => ({...p, nationalInsuranceNumber: e.target.value.toUpperCase().replace(/\s/g, '')}))}
                  placeholder="e.g. AB123456C"
                  maxLength={9}
                />
                <p className="text-xs text-gray-500 mt-1">Stored encrypted at rest. Used for HMRC reporting only.</p>
              </div>
              <div>
                <Label>Right to work status</Label>
                <select
                  value={form.rightToWorkStatus}
                  onChange={e => setForm(p => ({...p, rightToWorkStatus: e.target.value}))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select…</option>
                  <option value="uk_citizen">UK / Irish citizen</option>
                  <option value="settled_status">EU Settled Status</option>
                  <option value="pre_settled">EU Pre Settled</option>
                  <option value="visa">Visa with right to work</option>
                </select>
              </div>
            </Section>

            <Section title="Background check consent *" subtitle="Standard for every UK food delivery platform.">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.crcConsent}
                  onChange={e => setForm(p => ({...p, crcConsent: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300 mt-0.5"
                />
                <span className="text-xs text-gray-700">
                  I consent to a <strong>Basic DBS check</strong> (unspent convictions only) being carried out as part of Ariv's
                  verification of delivery riders. The same check Uber Eats, Deliveroo, and Just Eat run. Ariv (or our
                  verification partner) commissions and pays for the check — you do not pay. Results take 1 to 3 working days.
                </span>
              </label>
            </Section>

            {isMotorised && (
              <Section title="Your vehicle" subtitle="Required for moped, motorcycle, and car. Insurance must be hire & reward.">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Make</Label>
                    <Input value={form.vehicleMake} onChange={e => setForm(p => ({...p, vehicleMake: e.target.value}))} placeholder="Honda" />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input value={form.vehicleModel} onChange={e => setForm(p => ({...p, vehicleModel: e.target.value}))} placeholder="PCX125" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Colour</Label>
                    <Input value={form.vehicleColour} onChange={e => setForm(p => ({...p, vehicleColour: e.target.value}))} placeholder="Black" />
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Input value={form.vehicleYear} onChange={e => setForm(p => ({...p, vehicleYear: e.target.value}))} placeholder="2020" />
                  </div>
                  <div>
                    <Label>Plate *</Label>
                    <Input
                      value={form.vehicleRegistrationPlate}
                      onChange={e => setForm(p => ({...p, vehicleRegistrationPlate: e.target.value.toUpperCase()}))}
                      placeholder="AB12 CDE"
                    />
                  </div>
                </div>
                <div>
                  <Label>MOT expiry</Label>
                  <Input type="date" value={form.vehicleMOTExpiry} onChange={e => setForm(p => ({...p, vehicleMOTExpiry: e.target.value}))} />
                  <p className="text-xs text-gray-500 mt-1">Required if your vehicle is 3 or more years old.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Insurance provider *</Label>
                    <Input value={form.vehicleInsuranceProvider} onChange={e => setForm(p => ({...p, vehicleInsuranceProvider: e.target.value}))} placeholder="Zego, Acorn, MCE…" />
                  </div>
                  <div>
                    <Label>Policy number *</Label>
                    <Input value={form.vehicleInsurancePolicyNumber} onChange={e => setForm(p => ({...p, vehicleInsurancePolicyNumber: e.target.value}))} placeholder="POL12345678" />
                  </div>
                </div>
                <div>
                  <Label>Insurance expiry</Label>
                  <Input type="date" value={form.vehicleInsuranceExpiry} onChange={e => setForm(p => ({...p, vehicleInsuranceExpiry: e.target.value}))} />
                </div>
              </Section>
            )}

            <Section title="Working with Ariv *" subtitle="How the working relationship is set up.">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.classificationAccepted}
                  onChange={e => setForm(p => ({...p, classificationAccepted: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300 mt-0.5"
                />
                <span className="text-xs text-gray-700">
                  I confirm I am registering as a <strong>self-employed independent contractor</strong>. I am
                  responsible for my own tax and National Insurance, I choose when and whether to go online and
                  which deliveries to accept, I may work for other platforms, and I am not an employee or worker
                  of Ariv. I understand Ariv will keep a record of this declaration.
                </span>
              </label>
            </Section>

            <div className="flex items-start gap-2">
              <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
              <span className="text-xs text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>.
              </span>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white h-12 text-base"
            >
              {loading ? (isAddRoleMode ? 'Adding role…' : 'Creating account…') : (isAddRoleMode ? 'Add Delivery Rider role' : 'Create Rider Account')}
            </Button>

            {!isAddRoleMode && (
              <p className="text-xs text-center text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#1F3A68] font-semibold underline">Log in</Link>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryRiderSignupPage;
